import { observer } from 'mobx-react-lite'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Radom, { PayoutAmountMode, PayoutFeeEstimate, WithdrawalAccount } from '../state/Radom'
import Treasury from '../state/Treasury'
import User from '../state/User'
import { getMethod } from '../util/Managed'
import { errorToast, formatAssetAmount, shortAddress, successToast, useTestMode } from '../util/Util'
import { PrimaryButton, SecondaryButton } from './Button'
import Dropdown, { DropdownContext, DropdownItem, DropdownSearchContainer, DropdownTextInput } from './Dropdown'
import { InputLabel } from './Input'
import LoadingBar from './LoadingBar'
import Modal, { ModalFooter } from './Modal'
import NumberInput from './NumberInput'
import BRLLogo from '../images/BRLLogo.svg'
import EURLogo from '../images/EUROLogo.svg'
import GBPLogo from '../images/GBPLogo.svg'
import MXNLogo from '../images/MXNLogo.svg'
import USDLogo from '../images/USDLogo.svg'

const ETHEREUM_USDC_TOKEN = '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48'
const SEPOLIA_USDC_TOKEN = '0xa4fce8264370437e718ae207805b4e6233638b9e'

const WithdrawalRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-grow: 1;
  cursor: default;
`

const SectionCard = styled.div<{ $embedded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $embedded }) => $embedded ? '14px' : '10px'};
  padding: ${({ $embedded }) => $embedded ? '0' : '10px'};
  border-radius: ${({ $embedded }) => $embedded ? '0' : '13px'};
  border: ${({ $embedded }) => $embedded ? '0' : '1px solid #eceff3'};
  background: ${({ $embedded }) => $embedded ? 'transparent' : '#fbfcfe'};
`

const AssetSummaryCard = styled.div`
  align-items: center;
  background: #fff;
  border: 1px solid #e7ebf2;
  border-radius: 13px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding: 11px 13px;
`

const EstimateRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 14px;
  min-height: 22px;
`

const EstimateLabel = styled.span`
  color: #6b7280;
  font-size: 12.5px;
`

const EstimateValue = styled.span`
  color: #20242d;
  font-size: 12.75px;
  font-weight: 500;
  text-align: right;
`

export interface WithdrawableAsset {
  network: string
  token?: string
  maxAmount: string
  treasuryId?: string
}

interface IProps {
  modalOpen: boolean
  onModalOpen: (open: boolean) => void
  assetToWithdraw?: WithdrawableAsset
  withdrawableAssets?: WithdrawableAsset[]
  embedded?: boolean
  initialWithdrawalAccountId?: string
  initialWithdrawalKind?: WithdrawalKind
  hideWithdrawalKindSelector?: boolean
  onWithdrawalKindChange?: (kind: WithdrawalKind) => void
  onCreateWithdrawalAccount?: (kind: WithdrawalKind) => void
}

type WithdrawalKind = 'cash' | 'crypto'
type CashWithdrawalCurrency = 'EUR' | 'USD' | 'GBP' | 'BRL' | 'MXN'

const cashCurrencyConfig: Array<{
  currency: CashWithdrawalCurrency
  label: string
  logo: string
  enabled: boolean
}> = [
  { currency: 'EUR', label: 'Euro', logo: EURLogo, enabled: true },
  { currency: 'USD', label: 'US Dollar', logo: USDLogo, enabled: true },
  { currency: 'GBP', label: 'British Pound', logo: GBPLogo, enabled: false },
  { currency: 'BRL', label: 'Brazilian Real', logo: BRLLogo, enabled: false },
  { currency: 'MXN', label: 'Mexican Peso', logo: MXNLogo, enabled: false }
]

const getWithdrawalAccountCurrency = (account?: WithdrawalAccount): CashWithdrawalCurrency | undefined => {
  if (account?.accountDetails.Fiat?.accountType.iban) {
    return 'EUR'
  }

  if (account?.accountDetails.Fiat?.accountType.us) {
    return 'USD'
  }
}

const AssetWithdrawalModal = observer((props: IProps): ReactElement => {
  const navigate = useNavigate()
  const [testMode] = useTestMode()

  const [amountToWithdraw, setWithdrawalAmount] = useState('')
  const [amountMode, setAmountMode] = useState<PayoutAmountMode>('recipient')
  const [withdrawalKind, setWithdrawalKind] = useState<WithdrawalKind>(props.initialWithdrawalKind ?? 'cash')
  const [cashCurrency, setCashCurrency] = useState<CashWithdrawalCurrency>('EUR')
  const [selectedAsset, setSelectedAsset] = useState<WithdrawableAsset>(
    props.assetToWithdraw ?? props.withdrawableAssets?.[0] ?? { maxAmount: '', network: 'Ethereum' }
  )
  const [withdrawalAccount, setWithdrawalAccount] = useState<WithdrawalAccount>()
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const senderPaysFees = false

  const [logoUrl, setLogoUrl] = useState('')
  const [assetName, setAssetName] = useState('')

  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [withdrawalAccounts, setWithdrawalAccounts] = useState<WithdrawalAccount[]>([])
  const [assetSearch, setAssetSearch] = useState('')
  const [accountSearch, setAccountSearch] = useState('')
  const [cashCurrencySearch, setCashCurrencySearch] = useState('')
  const [feeEstimate, setFeeEstimate] = useState<PayoutFeeEstimate | null>(null)
  const [isEstimatingFee, setIsEstimatingFee] = useState(false)
  const isSameAsset = (a: WithdrawableAsset, b: WithdrawableAsset): boolean =>
    a.network === b.network &&
    a.token === b.token &&
    a.treasuryId === b.treasuryId
  const selectedTreasuryId = selectedAsset.treasuryId
  const selectedMethod = useMemo(
    () => getMethod(selectedAsset.network, selectedAsset.token),
    [selectedAsset]
  )
  const selectedFiatAccount = withdrawalAccount?.accountDetails.Fiat
  const selectedOwnerType = selectedFiatAccount?.accountOwnerType?.toLowerCase()
  const isSelectedIban = !!selectedFiatAccount?.accountType.iban
  const isSelectedUsAccount = !!selectedFiatAccount?.accountType.us
  const isSelectedBusinessIban = selectedOwnerType === 'business' && isSelectedIban
  const isSelectedIndividualIban = selectedOwnerType === 'individual' && isSelectedIban
  const usesLegacyEurOfframp = User.organization?.legacyEurOfframpEnabled === true
  const isIvyEurWithdrawal = withdrawalKind === 'cash' && cashCurrency === 'EUR' && !usesLegacyEurOfframp
  const expectedUsdcNetwork = testMode ? 'SepoliaTestnet' : 'Ethereum'
  const expectedUsdcToken = (testMode ? SEPOLIA_USDC_TOKEN : ETHEREUM_USDC_TOKEN).toLowerCase()
  const isSupportedIvyFundingAsset =
    selectedAsset.network === expectedUsdcNetwork &&
    (selectedMethod.ticker === 'USDC' || selectedAsset.token?.toLowerCase() === expectedUsdcToken)
  const isIvyBankWithdrawal = isIvyEurWithdrawal && isSelectedBusinessIban
  const availableAmount = selectedAsset.maxAmount
  const availableAmountLabel = `${formatAssetAmount(availableAmount, selectedMethod.ticker)} ${selectedMethod.ticker}`
  const estimatedTotalDebit = feeEstimate?.estimatedTotalDebitUsd
  const hasInsufficientBankFunding = isIvyEurWithdrawal &&
    !!estimatedTotalDebit &&
    Number(estimatedTotalDebit) > Number(availableAmount || 0)

  const filterWithdrawalAccount = (a: WithdrawalAccount): boolean => {
    if (selectedTreasuryId && !a.accountDetails.Crypto) {
      return false
    }

    if (withdrawalKind === 'crypto' && a.accountDetails.Crypto && a.accountDetails.Crypto.network === selectedAsset.network) {
      return true
    }

    if (withdrawalKind === 'cash' && a.accountDetails.Fiat) {
      const accountCurrency = getWithdrawalAccountCurrency(a)
      if (accountCurrency !== cashCurrency) {
        return false
      }

      if (accountCurrency === 'USD') {
        return !!a.accountDetails.Fiat.accountType.us
      }

      const isBusinessIban =
        a.accountDetails.Fiat.accountOwnerType?.toLowerCase() === 'business' &&
        !!a.accountDetails.Fiat.accountType.iban
      if (!usesLegacyEurOfframp && !isBusinessIban) {
        return false
      }

      return usesLegacyEurOfframp || isSupportedIvyFundingAsset
    }

    return false
  }

  const allAssets = props.withdrawableAssets?.length
    ? props.withdrawableAssets
    : [selectedAsset]
  const cashFundingAssets = allAssets.filter(asset => {
    if (asset.treasuryId) {
      return false
    }

    const method = getMethod(asset.network, asset.token)
    const token = asset.token?.toLowerCase()
    if (cashCurrency !== 'EUR' || usesLegacyEurOfframp) {
      return Number(asset.maxAmount) > 0
    }

    return asset.network === expectedUsdcNetwork &&
      (method.ticker === 'USDC' || token === expectedUsdcToken)
  })
  const selectableAssets = withdrawalKind === 'cash'
    ? cashFundingAssets
    : allAssets.filter(asset => Number(asset.maxAmount) > 0)
  const searchedCashCurrencyOptions = cashCurrencyConfig.filter(option => {
    const query = cashCurrencySearch.trim().toLowerCase()
    if (!query) return true
    return [option.currency, option.label].some(term => term.toLowerCase().includes(query))
  })
  const searchedSelectableAssets = selectableAssets.filter(asset => {
    const query = assetSearch.trim().toLowerCase()
    if (!query) {
      return true
    }

    const method = getMethod(asset.network, asset.token)
    const terms = [
      method.ticker,
      method.name,
      method.currencyName,
      method.hostChain?.name,
      asset.network,
      asset.token,
      asset.maxAmount
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })

  const filteredAccounts = withdrawalAccounts
    .filter(a => filterWithdrawalAccount(a))
  const searchedAccounts = filteredAccounts.filter(account => {
    const query = accountSearch.trim().toLowerCase()
    if (!query) {
      return true
    }

    const fiat = account.accountDetails.Fiat
    const crypto = account.accountDetails.Crypto
    const terms = [
      account.name,
      account.id,
      crypto?.address,
      crypto?.network,
      fiat?.accountOwnerName,
      fiat?.accountOwnerType,
      fiat?.accountType.iban?.iban,
      fiat?.accountType.iban?.bic,
      fiat?.accountType.us?.accountNumber,
      fiat?.accountType.us?.routingNumber,
      fiat?.accountType.us?.bankName
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })
  const hasIndividualIbanWithdrawalAccounts = withdrawalAccounts.some(a =>
    a.accountDetails.Fiat?.accountType.iban &&
    a.accountDetails.Fiat.accountOwnerType?.toLowerCase() === 'individual')
  const showIndividualIbanPayoutHint = isSupportedIvyFundingAsset &&
    withdrawalKind === 'cash' &&
    cashCurrency === 'EUR' &&
    filteredAccounts.length === 0 &&
    hasIndividualIbanWithdrawalAccounts

  useEffect(() => {
    if (withdrawalAccount && !filterWithdrawalAccount(withdrawalAccount)) {
      setWithdrawalAccount(undefined)
    }
  }, [selectedAsset, withdrawalAccount, withdrawalKind, cashCurrency])

  useEffect(() => {
    setWithdrawalAccount(undefined)
    setWithdrawalAmount('')
    setFeeEstimate(null)
  }, [cashCurrency])

  useEffect(() => {
    if (props.initialWithdrawalKind && props.initialWithdrawalKind !== withdrawalKind) {
      setWithdrawalKind(props.initialWithdrawalKind)
    }
  }, [props.initialWithdrawalKind])

  useEffect(() => {
    const nextAsset = withdrawalKind === 'cash'
      ? cashFundingAssets[0]
      : props.assetToWithdraw ?? allAssets[0]

    if (nextAsset && !selectableAssets.some(asset => isSameAsset(asset, selectedAsset))) {
      setSelectedAsset(nextAsset)
    }
  }, [withdrawalKind, cashCurrency, props.assetToWithdraw, props.withdrawableAssets?.length])

  useEffect(() => {
    const freshSelectedAsset = props.withdrawableAssets?.find(asset => isSameAsset(asset, selectedAsset))
    if (freshSelectedAsset && freshSelectedAsset.maxAmount !== selectedAsset.maxAmount) {
      setSelectedAsset(freshSelectedAsset)
    }
  }, [props.withdrawableAssets, selectedAsset])

  const renderAccountText = (account: WithdrawalAccount): React.ReactElement => {
    const accountName = account.name?.trim() || 'Withdrawal account'

    return <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, width: '100%' }}>
      {
        account.accountDetails.Crypto &&
        <img
          src={getMethod(account.accountDetails.Crypto.network).logo}
          style={{
            height: 20,
            width: 'auto',
            maxWidth: 20,
            flexShrink: 0
          }} />
      }
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{
          color: '#20242d',
          fontSize: 13.5,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {accountName}
        </span>
        {
          account.accountDetails.Crypto &&
          <span style={{ color: '#7d8492', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {shortAddress(account.accountDetails.Crypto.address)}
          </span>
        }
        {
          account.accountDetails.Fiat?.accountType.iban &&
          <span style={{ color: '#7d8492', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            SEPA · {account.accountDetails.Fiat.accountType.iban.iban}
          </span>
        }
        {
          account.accountDetails.Fiat?.accountType.us &&
          <span style={{ color: '#7d8492', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ACH · {account.accountDetails.Fiat.accountType.us.accountNumber}
          </span>
        }
      </div>
    </div>
  }

  const renderAssetText = (asset: WithdrawableAsset): React.ReactElement => {
    const method = getMethod(asset.network, asset.token)
    const balanceLabel = `Available ${formatAssetAmount(asset.maxAmount, method.ticker)} ${method.ticker}`

    return <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, width: '100%' }}>
      <img
        src={method.logo}
        style={{
          height: 20,
          width: 'auto',
          maxWidth: 20,
          flexShrink: 0
        }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{
          color: '#20242d',
          fontSize: 13.5,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {method.ticker} on {method.hostChain?.name ?? method.name}
        </span>
        <span style={{ color: '#7d8492', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {asset.treasuryId ? `Private treasury · ${balanceLabel}` : balanceLabel}
        </span>
      </div>
    </div>
  }

  const getNoSelectableAssetMessage = (): string => {
    if (withdrawalKind === 'crypto') {
      return 'No funded crypto assets are currently available.'
    }

    if (usesLegacyEurOfframp) {
      return 'No funded assets are currently available for bank withdrawals.'
    }

    if (cashCurrency === 'EUR') {
      return `EUR bank withdrawals currently require USDC on ${expectedUsdcNetwork}.`
    }

    return 'No funded assets are currently available for this cash withdrawal currency.'
  }

  const renderCashCurrencyText = (
    option: typeof cashCurrencyConfig[number],
    selected = false
  ): React.ReactElement => (
    <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0, opacity: option.enabled ? 1 : 0.45 }}>
      <img
        src={option.logo}
        style={{
          height: 20,
          width: 20,
          flexShrink: 0
        }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{
          color: '#20242d',
          fontSize: 13.5,
          fontWeight: 500,
          lineHeight: 1.2
        }}>
          {option.currency}
        </span>
        {!selected && <span style={{
          color: '#7d8492',
          fontSize: 12.5,
          lineHeight: 1.25
        }}>
          {option.enabled ? option.label : `${option.label} · Coming soon`}
        </span>}
      </div>
    </div>
  )

  useEffect(() => {
    setIsLoadingAccounts(true)
    Radom
      .listWithdrawalAccounts()
      .then(a => setWithdrawalAccounts(a))
      .finally(() => setIsLoadingAccounts(false))
  }, [])

  useEffect(() => {
    setLogoUrl(selectedMethod.logo)
    setAssetName(selectedMethod.name)

    setWithdrawalAccount(undefined)
    setWithdrawalAmount('')
    setFeeEstimate(null)
  }, [selectedAsset, selectedMethod, withdrawalKind, amountMode])

  useEffect(() => {
    if (!props.initialWithdrawalAccountId) {
      return
    }

    const preferredAccount = withdrawalAccounts.find(a => a.id === props.initialWithdrawalAccountId)
    if (preferredAccount && filterWithdrawalAccount(preferredAccount)) {
      setWithdrawalAccount(preferredAccount)
    }
  }, [props.initialWithdrawalAccountId, withdrawalAccounts, selectedAsset, withdrawalKind])

  useEffect(() => {
    if (!isIvyEurWithdrawal || !amountToWithdraw || Number(amountToWithdraw) <= 0) {
      setFeeEstimate(null)
      setIsEstimatingFee(false)
      return
    }

    let cancelled = false
    setIsEstimatingFee(true)
    const timeout = window.setTimeout(() => {
      Radom.getPayoutFee({
        currency: 'EUR',
        amount: amountToWithdraw,
        amountMode,
        accountType: 'business'
      })
        .then((estimate) => {
          if (!cancelled) {
            setFeeEstimate(estimate)
          }
        })
        .catch((err) => {
          console.error('Failed to estimate bank withdrawal fee', err)
          if (!cancelled) {
            setFeeEstimate(null)
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsEstimatingFee(false)
          }
        })
    }, 250)

    return () => {
      cancelled = true
      window.clearTimeout(timeout)
    }
  }, [amountToWithdraw, amountMode, isIvyEurWithdrawal])

  const onTokenWithdrawal = async (): Promise<void> => {
    if (!withdrawalAccount) {
      return
    }

    let isError = false

    setIsWithdrawing(true)
    const { network, token } = selectedAsset
    try {
      if (isSelectedIndividualIban && !usesLegacyEurOfframp) {
        throw new Error('Withdrawals to IBANs that belong to individuals must be sent from Payouts. Go to Payouts > Create payout and choose the customer IBAN payout account.')
      }

      if (isSelectedIban && !usesLegacyEurOfframp && !isSelectedBusinessIban) {
        throw new Error('This IBAN cannot be used from the withdrawal modal. Business-owned EUR IBANs can be withdrawn to here; individual IBANs must be paid from Payouts.')
      }

      if (isIvyBankWithdrawal) {
        if (!isSelectedBusinessIban) {
          throw new Error('EUR bank withdrawals require a business-owned IBAN account')
        }

        if (!isSupportedIvyFundingAsset) {
          throw new Error('EUR bank withdrawals are funded with USDC on Ethereum')
        }

        if (hasInsufficientBankFunding) {
          throw new Error('Available USDC balance is lower than the estimated bank withdrawal debit')
        }

        const user = await User.getUser()
        const createdPayout = await Radom.createPayout({
          payouts: [{
            gateway: 'fiatWithdrawalAccount',
            amount: amountToWithdraw,
            amountMode,
            withdrawalAccountId: withdrawalAccount.id,
            accountType: 'Business',
            email: user.emailAddress || '',
            name: withdrawalAccount.accountDetails.Fiat?.accountOwnerName
          }],
          accountType: 'Business',
          isTestMode: testMode,
          deductFees: false,
          sendEmailReceipts: false
        })

        await Radom.confirmPayout(createdPayout.id)
        successToast('Successfully submitted bank withdrawal')
      } else {
        if (withdrawalKind === 'cash' && cashCurrency === 'USD' && !isSelectedUsAccount) {
          throw new Error('USD bank withdrawals require a saved US bank account.')
        }

        await Radom.submitWithdrawalRequest(
          network,
          amountToWithdraw,
          withdrawalAccount.id,
          senderPaysFees,
          token,
          selectedTreasuryId
        )
        successToast('Successfully submitted withdrawal request')
      }
      Treasury.loadBalances()
    } catch (err) {
      isError = true
      console.error(err)
      errorToast(err.response?.data?.error || err.message)
    }
    setIsWithdrawing(false)

    if (!isError) {
      props.onModalOpen(false)
    }
  }

  const formatAmount = (value?: number | string | null, decimals = 6): string => {
    if (value === undefined || value === null || value === '') return '-'
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return String(value)
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    })
  }

  const hasSelectableAssets = selectableAssets.length > 0
  const isSelectedAssetSelectable = selectableAssets.some(asset => isSameAsset(asset, selectedAsset))
  const hasPositiveAmount = Number(amountToWithdraw) > 0
  const canSubmit =
    hasPositiveAmount &&
    isSelectedAssetSelectable &&
    !!withdrawalAccount &&
    !isEstimatingFee &&
    (!isIvyEurWithdrawal || (
      isSelectedBusinessIban &&
      isSupportedIvyFundingAsset &&
      !!feeEstimate &&
      !hasInsufficientBankFunding
    ))

  const cardContent = (
    <SectionCard $embedded={props.embedded}>
          {!props.hideWithdrawalKindSelector && <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <InputLabel style={{ opacity: 1 }}>Withdrawal type</InputLabel>
            <div style={{
              background: '#f4f6fa',
              border: '1px solid #e7ebf2',
              borderRadius: 12,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              padding: 3
            }}>
              {[
                { key: 'cash' as WithdrawalKind, label: 'Cash' },
                { key: 'crypto' as WithdrawalKind, label: 'Crypto' }
              ].map(option =>
                <SecondaryButton
                  key={option.key}
                  onClick={() => {
                    setWithdrawalKind(option.key)
                    props.onWithdrawalKindChange?.(option.key)
                  }}
                  style={{
                    background: withdrawalKind === option.key ? '#ffffff' : 'transparent',
                    border: withdrawalKind === option.key ? '1px solid #e1e6ee' : '1px solid transparent',
                    boxShadow: withdrawalKind === option.key ? '0 1px 2px rgba(20, 28, 45, 0.06)' : 'none',
                    color: withdrawalKind === option.key ? '#20242d' : '#697386',
                    minHeight: 34,
                    padding: '0 10px'
                  }}>
                  {option.label}
                </SecondaryButton>
              )}
            </div>
          </div>}

          {withdrawalKind === 'cash' && <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <InputLabel style={{ opacity: 1 }}>Cash currency</InputLabel>
            <Dropdown
              matchTriggerWidth
              onClose={() => setCashCurrencySearch('')}
              outerStyle={{ minHeight: 40, width: '100%' }}
              selectedContent={renderCashCurrencyText(
                cashCurrencyConfig.find(option => option.currency === cashCurrency) ?? cashCurrencyConfig[0],
                true
              )}
              dropdownContent={
                <DropdownContext.Consumer>
                  {({ closeDropdown }) => <div>
                    <DropdownSearchContainer>
                      <DropdownTextInput
                        value={cashCurrencySearch}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setCashCurrencySearch(e.target.value)}
                        placeholder='Search currencies'
                        style={{
                          border: '1px solid #d8dde7',
                          borderRadius: 10,
                          boxSizing: 'border-box',
                          minHeight: 38,
                          padding: '0 10px'
                        }}
                        autoFocus />
                    </DropdownSearchContainer>
                    {searchedCashCurrencyOptions.map(option => <DropdownItem
                      key={option.currency}
                      style={{
                        cursor: option.enabled ? 'pointer' : 'not-allowed',
                        color: '#20242d',
                        fontSize: 13.5,
                        fontWeight: 500,
                        lineHeight: 1.25,
                        pointerEvents: option.enabled ? undefined : 'none'
                      }}
                      onClick={() => {
                        if (!option.enabled) {
                          return
                        }

                        setCashCurrency(option.currency)
                        closeDropdown()
                      }}>
                      {renderCashCurrencyText(option)}
                    </DropdownItem>)}
                  </div>}
                </DropdownContext.Consumer>
              } />
          </div>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <InputLabel style={{ opacity: 1 }}>{withdrawalKind === 'cash' ? 'Funding asset' : 'Asset'}</InputLabel>
            {
              selectableAssets.length > 0
                ? <Dropdown
                  onClose={() => setAssetSearch('')}
                  outerStyle={{ minHeight: 40 }}
                  selectedContent={renderAssetText(selectedAsset)}
                  dropdownContent={
                    <DropdownContext.Consumer>
                      {({ closeDropdown }) => <div style={{ minWidth: 320 }}>
                        <DropdownSearchContainer>
                          <DropdownTextInput
                            value={assetSearch}
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => e.stopPropagation()}
                            onChange={e => setAssetSearch(e.target.value)}
                            placeholder='Search assets'
                            style={{
                              border: '1px solid #d8dde7',
                              borderRadius: 10,
                              boxSizing: 'border-box',
                              minHeight: 38,
                              padding: '0 10px'
                            }}
                            autoFocus />
                        </DropdownSearchContainer>
                        {searchedSelectableAssets.map(asset =>
                          <DropdownItem
                            key={`${asset.network}-${asset.token ?? 'native'}-${asset.treasuryId ?? 'radom'}`}
                            onClick={() => {
                              setSelectedAsset(asset)
                              closeDropdown()
                            }}
                            style={{ padding: '11px 12px' }}>
                            {renderAssetText(asset)}
                          </DropdownItem>
                        )}
                        {searchedSelectableAssets.length === 0 &&
                          <div style={{
                            color: '#7d8492',
                            fontSize: 12.5,
                            lineHeight: 1.45,
                            padding: '0 12px 14px'
                          }}>
                            No assets match this search.
                          </div>}
                      </div>}
                    </DropdownContext.Consumer>
                  } />
                : <div style={{
                  background: '#ffffff',
                  border: '1px solid #e8edf5',
                  borderRadius: 12,
                  color: '#7d8492',
                  fontSize: 12.5,
                  lineHeight: 1.45,
                  padding: '10px 12px'
                }}>
                  {getNoSelectableAssetMessage()}
                </div>
            }
          </div>

          {hasSelectableAssets && <>
            <AssetSummaryCard>
              <WithdrawalRow>
                <div style={{
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                  backgroundImage: `url(${logoUrl})`,
                  minWidth: 34,
                  height: 34,
                  flexShrink: 0
                }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                  <span style={{ fontSize: 11.25, opacity: 0.62, textTransform: 'uppercase', letterSpacing: 0 }}>{assetName}</span>
                  <span style={{ color: '#20242d', fontSize: 15.25, fontWeight: 500, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    Ready to withdraw
                  </span>
                </div>
              </WithdrawalRow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', textAlign: 'right' }}>
                <span style={{ color: '#6b7280', fontSize: 11.5 }}>Available balance</span>
                <span style={{
                  color: '#20242d',
                  fontSize: 15.25,
                  fontWeight: 500,
                  maxWidth: 190,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {availableAmountLabel}
                </span>
              </div>
            </AssetSummaryCard>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              <InputLabel style={{ opacity: 1 }}>
                {withdrawalKind === 'cash'
                  ? isIvyEurWithdrawal
                    ? amountMode === 'recipient' ? 'Recipient receives' : 'You pay'
                    : 'Funding amount'
                  : 'Withdrawal amount'}
              </InputLabel>
              {
                withdrawalKind === 'cash' && isIvyEurWithdrawal &&
                <div style={{
                  background: '#f4f6fa',
                  border: '1px solid #e7ebf2',
                  borderRadius: 12,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  padding: 3
                }}>
                  {[
                    { key: 'recipient' as PayoutAmountMode, label: 'EUR amount' },
                    { key: 'source' as PayoutAmountMode, label: 'USDC debit' }
                  ].map(option =>
                    <SecondaryButton
                      key={option.key}
                      onClick={() => setAmountMode(option.key)}
                      style={{
                        background: amountMode === option.key ? '#ffffff' : 'transparent',
                        border: amountMode === option.key ? '1px solid #e1e6ee' : '1px solid transparent',
                        boxShadow: amountMode === option.key ? '0 1px 2px rgba(20, 28, 45, 0.06)' : 'none',
                        color: amountMode === option.key ? '#20242d' : '#697386',
                        minHeight: 32,
                        padding: '0 10px'
                      }}>
                      {option.label}
                    </SecondaryButton>
                  )}
                </div>
              }
              <div style={{ display: 'flex', flexGrow: 1, gap: 10 }}>
                <NumberInput
                  disabled={isWithdrawing}
                  value={amountToWithdraw}
                  onUserInput={e => setWithdrawalAmount(e)}
                  fontSize='14.5'
                  align='left'
                  style={{ minHeight: 40 }}
                  placeholder='Enter amount'
                  required={true}
                />
                {
                  withdrawalKind === 'cash'
                    ? <>
                      <div style={{
                        alignItems: 'center',
                        border: '1px solid #e1e6ee',
                        borderRadius: 10,
                        color: '#20242d',
                        display: 'flex',
                        fontSize: 13,
                        fontWeight: 500,
                        minHeight: 40,
                        padding: '0 12px'
                      }}>{isIvyEurWithdrawal ? amountMode === 'recipient' ? 'EUR' : 'USDC' : selectedMethod.ticker}</div>
                      {
                        (!isIvyEurWithdrawal || amountMode === 'source') &&
                        <SecondaryButton
                          style={{ minHeight: 40, minWidth: 66, padding: '0 12px', flexShrink: 0 }}
                          disabled={isWithdrawing}
                          onClick={() => setWithdrawalAmount(availableAmount)}>
                          Max
                        </SecondaryButton>
                      }
                    </>
                    : <SecondaryButton
                      style={{ minHeight: 40, minWidth: 66, padding: '0 12px', flexShrink: 0 }}
                      disabled={isWithdrawing}
                      onClick={() => setWithdrawalAmount(availableAmount)}>
                      Max
                    </SecondaryButton>
                }
              </div>
            </div>

            {
              isIvyEurWithdrawal &&
            <div style={{
              background: '#ffffff',
              border: `1px solid ${hasInsufficientBankFunding ? '#f1c7c7' : '#e8edf5'}`,
              borderRadius: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 7,
              padding: '10px 12px'
            }}>
              {
                isEstimatingFee
                  ? <LoadingBar style={{ borderRadius: 25, width: '100%', height: 28 }} />
                  : <>
                    {
                      amountMode === 'source' &&
                      <EstimateRow>
                        <EstimateLabel>Recipient receives</EstimateLabel>
                        <EstimateValue>{formatAmount(feeEstimate?.estimatedRecipientAmount, 2)} EUR</EstimateValue>
                      </EstimateRow>
                    }
                    <EstimateRow>
                      <EstimateLabel>You pay</EstimateLabel>
                      <EstimateValue>{formatAmount(feeEstimate?.estimatedTotalDebitUsd)} USDC</EstimateValue>
                    </EstimateRow>
                    <EstimateRow>
                      <EstimateLabel>Funding amount</EstimateLabel>
                      <EstimateValue>{formatAmount(feeEstimate?.estimatedFundingAmountUsd)} USDC</EstimateValue>
                    </EstimateRow>
                    <EstimateRow>
                      <EstimateLabel>Payout fee</EstimateLabel>
                      <EstimateValue>{formatAmount(feeEstimate?.estimatedFeeUsd)} USDC</EstimateValue>
                    </EstimateRow>
                    <EstimateRow>
                      <EstimateLabel>Rate</EstimateLabel>
                      <EstimateValue>1 USDC ≈ {formatAmount(feeEstimate?.exchangeRate, 8)} EUR</EstimateValue>
                    </EstimateRow>
                  </>
              }
              {
                hasInsufficientBankFunding &&
                <span style={{ color: '#b42318', fontSize: 12.25, lineHeight: 1.4 }}>
                  Available USDC balance is lower than the estimated debit.
                </span>
              }
            </div>
            }

            {selectedTreasuryId && <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              padding: '10px 12px',
              borderRadius: 12,
              border: '1px solid #e8edf5',
              background: '#ffffff'
            }}>
              <span style={{ color: '#20242d', fontSize: 13, fontWeight: 500 }}>Private treasury</span>
              <span style={{ color: '#7d8492', fontSize: 12.5, lineHeight: 1.45 }}>
                This withdrawal will be sent directly from the organization&apos;s private treasury.
              </span>
            </div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              <InputLabel style={{ opacity: 1 }}>Withdrawal destination</InputLabel>
              {
                isLoadingAccounts
                  ? <LoadingBar style={{ borderRadius: 25, width: '100%', height: 40 }} />
                  : filteredAccounts.length > 0
                    ? <Dropdown
                      disabled={isLoadingAccounts}
                      onClose={() => setAccountSearch('')}
                      outerStyle={{ minHeight: 40 }}
                      selectedContent={
                        withdrawalAccount
                          ? renderAccountText(withdrawalAccount)
                          : <span style={{ fontSize: 13.5, color: '#747894' }}>Select account</span>
                      }
                      dropdownContent={
                        <DropdownContext.Consumer>
                          {({ closeDropdown }) => <div style={{ minWidth: 320 }}>
                            <DropdownSearchContainer>
                              <DropdownTextInput
                                value={accountSearch}
                                onMouseDown={e => e.stopPropagation()}
                                onClick={e => e.stopPropagation()}
                                onChange={e => setAccountSearch(e.target.value)}
                                placeholder='Search accounts'
                                style={{
                                  border: '1px solid #d8dde7',
                                  borderRadius: 10,
                                  boxSizing: 'border-box',
                                  minHeight: 38,
                                  padding: '0 10px'
                                }}
                                autoFocus />
                            </DropdownSearchContainer>
                            {searchedAccounts
                              .filter(t => t.id !== withdrawalAccount?.id)
                              .map(t =>
                                <DropdownItem
                                  key={t.id}
                                  onClick={() => {
                                    setWithdrawalAccount(t)
                                    closeDropdown()
                                  }}
                                  style={{ padding: '11px 12px' }}>
                                  {renderAccountText(t)}
                                </DropdownItem>
                              )}
                            {searchedAccounts.filter(t => t.id !== withdrawalAccount?.id).length === 0 &&
                            <div style={{
                              color: '#7d8492',
                              fontSize: 12.5,
                              lineHeight: 1.45,
                              padding: '8px 12px 14px'
                            }}>
                              No accounts match this search.
                            </div>}
                          </div>}
                        </DropdownContext.Consumer>
                      } />
                    : showIndividualIbanPayoutHint
                      ? <div style={{
                        background: '#ffffff',
                        border: '1px solid #e8edf5',
                        borderRadius: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        padding: '10px 12px'
                      }}>
                        <span style={{ color: '#20242d', fontSize: 13, fontWeight: 500 }}>
                        Individual IBAN recipients are paid from Payouts
                        </span>
                        <span style={{ color: '#7d8492', fontSize: 12.5, lineHeight: 1.45 }}>
                          Use this withdrawal modal for business-owned EUR IBANs. To pay an individual&apos;s IBAN,
                          create a payout and choose their IBAN payout account.
                        </span>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <SecondaryButton onClick={() => {
                            navigate('/payouts/create')
                            props.onModalOpen(false)
                          }} style={{ minHeight: 36 }}>Create payout</SecondaryButton>
                          <SecondaryButton onClick={() => {
                            if (props.onCreateWithdrawalAccount) {
                              props.onCreateWithdrawalAccount('cash')
                            } else {
                              navigate('/accounts')
                              props.onModalOpen(false)
                            }
                          }} style={{ minHeight: 36 }}>Add business IBAN</SecondaryButton>
                        </div>
                      </div>
                      : <SecondaryButton onClick={() => {
                        if (props.onCreateWithdrawalAccount) {
                          props.onCreateWithdrawalAccount(withdrawalKind)
                        } else {
                          navigate('/accounts')
                          props.onModalOpen(false)
                        }
                      }} style={{ width: '100%', minHeight: 40 }}>Create withdrawal account</SecondaryButton>

              }
            </div>
          </>}

    </SectionCard>
  )

  const footerContent = hasSelectableAssets && filteredAccounts.length > 0 && (
    <ModalFooter $fullWidthAction>
      <PrimaryButton
        disabled={!canSubmit}
        isLoading={isWithdrawing}
        style={{ width: '100%' }}
        onClick={onTokenWithdrawal}>
        {withdrawalKind === 'cash' ? 'Withdraw to bank' : 'Withdraw'}
      </PrimaryButton>
    </ModalFooter>
  )

  if (props.embedded) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cardContent}
          {footerContent}
        </div>
      </div>
    )
  }

  return (
    <Modal
      visible={props.modalOpen}
      innerStyles={{ width: 476 }}
      onClose={() => props.onModalOpen(false)}
      title='Withdraw'>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {cardContent}
      </div>
      {footerContent}
    </Modal>
  )
})

export default AssetWithdrawalModal
